import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, RadioGroup, Radio, Alert, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setInvoiceDetailsData } from '../../../redux/invoiceDetailsDataSlice';
import { CustomSingleSelect } from '../../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Iconify from '../../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import '../../../css/invoices/addInvoiceDialog.css';

export const EditInvoiceMainData = ({ openEditInvoiceMainData, setOpenEditInvoiceMainData }) => {

    const { id } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const invoiceDetailsData = useSelector((state) => state.InvoiceDetailsData);

    const [errMsg, setErrMsg] = useState('');
    const [forContract, setForContract] = useState(invoiceDetailsData.contract?.id ? true : false);

    const formik = useFormik({

        initialValues: {
            title: invoiceDetailsData?.title || '',
            invoice_number: invoiceDetailsData?.invoice_number || '',
            company: { value: invoiceDetailsData?.company?.id, label: invoiceDetailsData?.company?.name } || '',
            contract: { value: invoiceDetailsData?.contract?.id, label: invoiceDetailsData?.contract?.title } || null,
            date: invoiceDetailsData?.date || '',
        },

        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            invoice_number: Yup.string().required(t('isRequired')),
            company: Yup.object().required(t('isRequired')),
            contract: forContract ? Yup.object().required(t('isRequired')) : Yup.object().optional(),
            date: Yup.date().required(t('isRequired')),
        }),

        onSubmit: async (values) => {

            const invoiceData = {
                title: values.title,
                invoice_number: values.invoice_number,
                company_id: values.company.value,
                contract_id: forContract ? values.contract.value : null,
                date: values.date,
            }
            try {
                const response = await axios.patch(`invoice/updateMainData/${id}`, invoiceData);

                if (response.status === 200 || response.status === 201) {
                    dispatch(setSuccessfullModifyDialog({ open: true, type: "Modifier les informations de la facture" }));
                    dispatch(setInvoiceDetailsData({ ...invoiceDetailsData, ...response.data }));
                    setOpenEditInvoiceMainData(false);
                }
            } catch (error) {
                console.error('Error updating invoice main data:', error);
                CustomErrorLogger(error, 'Error adding invoice main data');
                setErrMsg(error.response?.data?.message || error.message || 'Une erreur est survenue lors de la mise à jour des données de la facture.');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenEditInvoiceMainData(false);
    }, [setOpenEditInvoiceMainData]);

    const fetchContracts = useCallback(async (page, limit, search) => {
        try {
            const response = await axios.get('contract/findAllForInvoice', {
                params: {
                    page,
                    limit,
                    search,
                },
            });

            if (response.status === 200 || response.status === 201) {
                return response.data.map(contract => ({
                    value: contract.id,
                    label: contract.contract_number
                        ? `${contract.title}, ${contract.contract_number} (${contract.company.name})`
                        : `${contract.title} (${contract.company.name})`,
                    company: contract.company,
                    contract_site: contract.contract_site,
                    machine: contract.contract_date[0].contract_machine,
                }));
            }

            return [];
        } catch (error) {
            console.error('Error fetching contracts:', error);
            CustomErrorLogger(error, "error fetching contracts in editInvoiceDialoge");
        }
    }, []);

    const handleCreateCompany = useCallback(async (newOption) => {
        try {
            const response = await axios.post('company', { name: newOption.label });
            if (response.status === 200 || response.status === 201) {
                setFieldValue('company', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company in editInvoiceDialoge:', error);
            CustomErrorLogger(error, "Error creating company in editInvoiceDialoge");
        }
    }, [setFieldValue]);

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in editInvoiceDialoge");
        }
    }, []);

    const onChangeContract = (value) => {

        setFieldValue('contract', value);
        if (value) {
            setFieldValue('company', { value: value.company.id, label: value.company.name });
            setFieldValue('machines', value.machine.map((machine) => ({ value: machine.machine.id, label: machine.machine.name })));
            return;
        }
        setFieldValue('company', '');
        setFieldValue('machines', []);
    };

    return (
        <Dialog open={openEditInvoiceMainData} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addInvoice_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addInvoice_close_icon' />
            </IconButton>
            <DialogTitle className='addInvoice_dialog_title'>Modification de la facture</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addInvoice_dialog_content'>
                    <Box className="addInvoice_title_number_box">
                        <Box className="addInvoice_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Numéro de la facture</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le numero'
                                value={values.invoice_number}
                                className='addInvoice_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="invoice_number"
                                error={touched.invoice_number && Boolean(errors.invoice_number)}
                                helperText={touched.invoice_number && errors.invoice_number}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                        <Box className="addInvoice_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Titre de la facture</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le titre'
                                value={values.title}
                                className='addInvoice_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                    </Box>
                    <Box className="addInvoice_title_number_box">
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Date de facture</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addInvoice_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.date && new Date(values.date).toISOString().split('T')[0]}
                                onChange={(event) => setFieldValue('date', event.target.value)}
                                onBlur={handleBlur}
                                name="date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.date && Boolean(errors.date)}
                                helperText={touched.date && errors.date}
                            />
                        </Box>
                    </Box>
                    <Box className="addInvoice_title_number_box">
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Type de facture</FormHelperText>
                            <RadioGroup
                                name="contractAssociation"
                                className='addInvoice_radio_group'
                                value={forContract}
                                onChange={(e) => setForContract(e.target.value === 'true')}
                            >
                                <Box className='addInvoice_radio_box'>
                                    <Radio value={false} />
                                    <Typography className="addInvoice_radio_label">Sans contrat</Typography>
                                </Box>
                                <Box className='addInvoice_radio_box'>
                                    <Radio value={true} />
                                    <Typography className="addInvoice_radio_label">Associée à un contrat</Typography>
                                </Box>
                            </RadioGroup>

                        </Box>
                    </Box>
                    {forContract &&
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Sélection du contrat concerné</FormHelperText>
                            <CustomSingleSelect
                                placeholder="Sélectionnez un contrat"
                                className='addInvoice_InputField'
                                required={true}
                                value={values.contract}
                                fetchFunction={fetchContracts}
                                backendSearchable={true}
                                onChange={onChangeContract}
                                helperText={touched.contract && errors.contract}
                                error={touched.contract && Boolean(errors.contract)}
                            />
                        </Box>
                    }
                    <Box className='addInvoice_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Entreprise associée au facture</FormHelperText>
                        {(forContract && values.company?.label) &&
                            <Box className="ships_box">
                                <Typography className="ships_label">{values.company?.label}</Typography>
                            </Box>
                        }
                        {!forContract &&
                            <CustomSingleSelect
                                placeholder='Sélectionnez une entreprise'
                                className='addInvoice_InputField'
                                required={true}
                                value={values.company}
                                fetchFunction={fetchCompanies}
                                backendSearchable={true}
                                onChange={(value) => setFieldValue('company', value)}
                                creatable={true}
                                disabled={forContract}
                                createFunction={handleCreateCompany}
                                helperText={touched.company && errors.company}
                                error={touched.company && Boolean(errors.company)}
                            />
                        }
                    </Box>
                </DialogContent>
                <DialogActions className='addInvoice_dialog_actions'>
                    <Button className='addInvoice_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addInvoice_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};